<script setup>
import { computed, useSlots } from 'vue'

const props = defineProps({
    alignment: String,
    compact: {
        type: Boolean,
        default: false,
    },
    cardStyle: {
        type: String,
        required: false,
        default: 'outlined',
    },
    highlight: {
        type: Boolean,
        default: false,
    },
    role: {
        type: String,
        required: false,
        default: 'primary'
    },
    overflow: {
        type: String,
        default: 'hidden',
    },
    padless: {
        type: Boolean,
        default: false,
    },
    padlessImage: {
        type: Boolean,
        default: false,
    },
    marginBottomClass: {
        type: String,
        required: false,
        default: '',
    },
})

const slots = useSlots()

const hasHeaderSlot = computed(() => !!slots.header)
const hasImageSlot = computed(() => !!slots.image)

const alignmentClass = computed(() => props.alignment === 'center' ? 'text-center' : '')
const highlightClass = computed(() => props.highlight ? 'card-highlight' : '')
const overflowClass = computed(() => `overflow-${props.overflow}`)

const paddingClass = computed(() => {
    if (props.padless) return ''
    return props.compact ? 'p-3' : 'p-4'
})

const padlessImageOffset = computed(() => {
    if (!props.padlessImage) return ''
    return props.compact ? '-mx-3 -mt-3' : '-mx-4 -mt-4'
})

const cardStyleClass = computed(() => {
    let classes = [
        'border',
        'border-gray-200'
    ];
    let bgClass = 'bg-white';

    if (props.cardStyle === 'elevated') {
        return 'border border-gray-200 hover:shadow-md transition-shadow';
    }

    if (props.role === 'secondary') {
        bgClass = 'bg-gray-50';
    }

    classes.push(bgClass)

    return classes.join(' ')
})
</script>

<template>
    <div
        class="rounded-xl"
        :class="[alignmentClass, highlightClass, paddingClass, overflowClass, marginBottomClass, cardStyleClass]"
    >
        <div v-if="hasHeaderSlot" class="mb-2">
            <span class="text-sm font-semibold text-gray-900">
                <slot name="header" />
            </span>
        </div>

        <div
            v-if="hasImageSlot"
            class="bg-white"
            :class="padlessImageOffset"
        >
            <slot name="image" />
        </div>

        <div>
            <slot name="body" />
            <slot />
        </div>
    </div>
</template>

<style scoped>
.card-highlight {
    border-color: var(--whitelabel-primary);
    box-shadow: var(--whitelabel-primary) 0px 0px 7px;
}
</style>
